import { Card, Typography, Form, Input, InputNumber, Select, Button, Row, Col, Space, Tag, Switch, Checkbox } from 'antd'
import React, { useEffect, useState } from 'react'
import {
    ArrowLeftOutlined, InfoCircleOutlined, EnvironmentOutlined, DollarOutlined,
    FileTextOutlined, BulbOutlined, LinkOutlined,
} from "@ant-design/icons"
import { SupplierService } from '../../../Services/Manage/SupplierService'
import useMessage from 'antd/es/message/useMessage'
import { useLocation, useNavigate } from 'react-router-dom'

const { TextArea } = Input;
const { Title, Text } = Typography;

const compactFormStyle = `
  .ant-form-item { margin-bottom: 15px !important; }
  .ant-card-body { padding: 16px 16px !important; }
  .ant-card { margin-bottom: 16px !important; }
  .ant-form-item-label { padding-bottom: 2px !important; }
  .ant-form-item-label > label { height: 20px !important; }
`;

const CardTitle = ({ icon, no, text }) => (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: "rgba(14, 93, 146, 0.95)" }}>
        {icon}
        <span style={{ fontWeight: 600 }}>{no}. {text}</span>
    </span>
);

// const currencyOptions = [
//     { value: "INR", label: "Indian Rupee (₹)" },
//     { value: "USD", label: "US Dollar ($)" },
//     { value: "AED", label: "UAE Dirham (AED)" },
// ];

const quickLinks = [
    { label: "View Suppliers", to: "/inventory/suppliers" },
    { label: "Supplier Categories", to: "/inventory/suppliers" },
    { label: "Purchase Orders", to: "/inventory/suppliers" },
    { label: "Goods Receipts", to: "/inventory/suppliers" },
    { label: "Supplier Ledger", to: "/inventory/suppliers" },
    { label: "Supplier Reports", to: "/inventory/suppliers" },
];

const AddSupplier = () => {
    const [messageApi, msgContext] = useMessage();
    const [form] = Form.useForm();
    const [supplierCode, setSupplierCode] = useState("SUP-001");
    const [categoryOptions, setCategoryOptions] = useState([]);
    const [supplierTypeOptions, setSupplierTypeOptions] = useState([]);
    const [paymentTermsOptions, setPaymentTermsOptions] = useState([]);
    const [paymentMethodOptions, setPaymentMethodOptions] = useState([]);
    const [contactMethodOptions, setContactMethodOptions] = useState([]);
    const [countries, setCountries] = useState([]);
    const [stateOptions, setStateOptions] = useState([]);

    const [editSupplier, setEditSupplier] = useState(null);
    const [saving, setSaving] = useState(false);
    const [sameAsBilling, setSameAsBilling] = useState(true);

    const location = useLocation();
    const navigate = useNavigate();
    const currentUser = (() => {
        try { return JSON.parse(localStorage.getItem("ome_user")) || {}; } catch { return {}; }
    })();
    const currentUserId = currentUser.uid;
    const id = location.state?.id;
    const action = location.state?.action;
    const enableEdit = location.state?.enableEdit;
    const from = location.state?.from || '/inventory/suppliers';
    const isReadOnly = !enableEdit && !!editSupplier;

    const summary = Form.useWatch([], form) || {};
    const firstOf = (v) => (Array.isArray(v) ? v[0] : v);

    useEffect(() => {
        getConfigOptions();
        getAllCountries();
        if (id) {
            getSupplierById(id);
        } else {
            getLastSupplier();
        }
    }, [id]);

    useEffect(() => {
        if (editSupplier?.country && countries.length) {
            const c = countries.find((x) => x.value === editSupplier.country);
            setStateOptions(c ? c.states : []);
        }
    }, [editSupplier, countries]);

    const getAllCountries = async () => {
        try {
            const res = await fetch("https://countriesnow.space/api/v0.1/countries/states");
            const json = await res.json();
            const list = (json.data || []).map((c) => ({
                value: c.name,
                label: c.name,
                states: (c.states || []).map((s) => ({ value: s.name, label: s.name })),
            }));
            setCountries(list);
        } catch (e) { console.error(e); }
    };

    const handleCountryChange = (countryName) => {
        const c = countries.find((x) => x.value === countryName);
        setStateOptions(c ? c.states : []);
        form.setFieldsValue({ state: null });
    };

    const getConfigOptions = async () => {
        try {
            const fields = ["category", "supplier_type", "payment_terms", "payment_method", "preferred_contact_method"];
            const results = await Promise.all(fields.map((f) => SupplierService.getDistinctValues(f)));
            const toOptions = (arr) => (arr || []).map((v) => ({ value: v, label: v }));

            setCategoryOptions(toOptions(results[0].data));
            setSupplierTypeOptions(toOptions(results[1].data));
            setPaymentTermsOptions(toOptions(results[2].data));
            setPaymentMethodOptions(toOptions(results[3].data));
            setContactMethodOptions(toOptions(results[4].data));
        } catch (error) {
            console.error(error);
        }
    };

    const getLastSupplier = async () => {
        try {
            const request = await SupplierService.getLastSupplier();
            let nextNo = "001";
            if (request?.status && request?.data?.supplier_code) {
                const seqPart = request.data.supplier_code.split("-").pop();
                const parsed = parseInt(seqPart, 10);
                if (!isNaN(parsed)) nextNo = String(parsed + 1).padStart(3, "0");
            }
            setSupplierCode(`SUP-${nextNo}`);
        } catch (error) {
            console.error(error);
        }
    };

    const getSupplierById = async (id) => {
        try {
            const request = await SupplierService.getSupplierById(id);
            const initialValues = request.data;
            setEditSupplier(initialValues);
            setSupplierCode(initialValues.supplier_code || "SUP-001");
            setSameAsBilling(!!initialValues.same_as_billing);

            form.setFieldsValue({
                ...initialValues,
                category: initialValues.category ? [initialValues.category] : [],
                supplier_type: initialValues.supplier_type ? [initialValues.supplier_type] : [],
                payment_terms: initialValues.payment_terms ? [initialValues.payment_terms] : [],
                payment_method: initialValues.payment_method ? [initialValues.payment_method] : [],
                preferred_contact_method: initialValues.preferred_contact_method ? [initialValues.preferred_contact_method] : [],
            });
        } catch (error) {
            console.error(error);
        }
    };

    const onBillingAddressChange = (e) => {
        if (sameAsBilling) {
            form.setFieldsValue({ shipping_address: e.target.value });
        }
    };

    const onSameAsBillingChange = (e) => {
        setSameAsBilling(e.target.checked);
        if (e.target.checked) {
            form.setFieldsValue({ shipping_address: form.getFieldValue('billing_address') });
        }
    };

    const onFinish = async (values) => {
        await performSave(values);
    };

    const handleSaveDraft = async () => {
        const values = form.getFieldsValue();
        await performSave(values);
    };

    const performSave = async (values) => {
        setSaving(true);
        try {
            const payload = {
                ...values,
                category: firstOf(values.category),
                supplier_type: firstOf(values.supplier_type),
                payment_terms: firstOf(values.payment_terms),
                payment_method: firstOf(values.payment_method),
                preferred_contact_method: firstOf(values.preferred_contact_method),
                same_as_billing: sameAsBilling,
                shipping_address: sameAsBilling ? values.billing_address : values.shipping_address,
                added_by: editSupplier ? editSupplier.added_by : currentUserId,
                supplier_code: supplierCode,
            };

            let request;
            if (editSupplier) {
                request = await SupplierService.updateSupplier(payload, id);
            } else {
                request = await SupplierService.createSupplier(payload);
            }

            if (request?.status) {
                messageApi.success(request.message);
                if (!editSupplier) {
                    form.resetFields();
                    setSameAsBilling(true);
                }
                navigate(from);
            } else {
                messageApi.error(request?.message || "Something went wrong");
            }
        } catch (error) {
            console.error("Error in onFinish:", error);
            messageApi.error("Unexpected error occurred");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            const request = await SupplierService.deleteSupplier(id);
            if (request.status) {
                messageApi.success(request.message);
                navigate(from);
            } else {
                messageApi.error(request.message);
            }
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <>
            <style>{compactFormStyle}</style>
            {msgContext}
            <Card
                title={
                    <Space align="center">
                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                            <ArrowLeftOutlined
                                style={{ cursor: "pointer", fontSize: "20px" }}
                                onClick={() => navigate(from)}
                            />
                            <Title level={4} style={{ margin: 0 }}>
                                <Space>
                                    {editSupplier ? `${action} Supplier` : 'Add Supplier'}{' '}
                                    {editSupplier && (
                                        <Tag color="cyan" style={{ fontSize: '16px', padding: '6px 16px', borderRadius: '8px', fontWeight: 600 }}>
                                            Code: {editSupplier.supplier_code}
                                        </Tag>
                                    )}
                                </Space>
                            </Title>
                        </div>
                    </Space>
                }
                extra={
                    action === 'View' ? null : !enableEdit && editSupplier ? (
                        <Button size="large" danger onClick={() => navigate("/inventory/suppliers/edit-supplier", { state: { id, action: 'Edit', enableEdit: true, from } })}>
                            Enable Edit
                        </Button>
                    ) : (
                        <Space>
                            <Button size="large" loading={saving} onClick={handleSaveDraft}>Save as Draft</Button>
                            <Button
                                type="primary" size="large" loading={saving} onClick={() => form.submit()}
                                style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                            >
                                {editSupplier ? "Update Supplier" : "Save Supplier"}
                            </Button>
                        </Space>
                    )
                }
            >
                <Row gutter={16}>
                    <Col xs={24} lg={17}>
                        <Form
                            layout="vertical"
                            form={form}
                            onFinish={onFinish}
                            size="middle"
                            disabled={isReadOnly}
                            initialValues={{ is_active: true, same_as_billing: true }}
                        >
                            <Card title={<CardTitle icon={<InfoCircleOutlined />} no={1} text="Basic Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="supplier_name" label="Supplier Name" rules={[{ required: true, message: 'Please enter supplier name' }]}>
                                            <Input size="large" placeholder="Enter supplier name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item label="Supplier Code">
                                            <Input size="large" readOnly value={supplierCode} placeholder="Auto Generate" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="category" label="Category">
                                            <Select options={categoryOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type category" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="supplier_type" label="Supplier Type">
                                            <Select options={supplierTypeOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type supplier type" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="contact_person" label="Contact Person">
                                            <Input size="large" placeholder="Enter contact person name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="designation" label="Designation">
                                            <Input size="large" placeholder="Enter designation" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="mobile_number" label="Mobile Number" rules={[{ required: true, message: 'Please enter mobile number' }]}>
                                            <Input size="large" placeholder="Enter mobile number" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="email_address" label="Email Address">
                                            <Input size="large" placeholder="Enter email address" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="website" label="Website">
                                            <Input size="large" placeholder="Enter website (optional)" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="gstin_tax_id" label="GSTIN / Tax ID">
                                            <Input size="large" placeholder="Enter GSTIN / Tax ID" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="pan_number" label="PAN Number">
                                            <Input size="large" placeholder="Enter PAN number" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="currency" label="Currency">
                                            <Input size="large" placeholder="Enter currency (e.g. INR, USD)" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<EnvironmentOutlined />} no={2} text="Address Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            name="billing_address" label="Billing Address"
                                            rules={[{ required: true, message: 'Please enter billing address' }]}
                                        >
                                            <TextArea rows={3} maxLength={500} showCount placeholder="Enter billing address" onChange={onBillingAddressChange} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            name="shipping_address"
                                            label={
                                                <Space>
                                                    <span>Shipping Address</span>
                                                    <Checkbox checked={sameAsBilling} onChange={onSameAsBillingChange}>Same as billing address</Checkbox>
                                                </Space>
                                            }
                                        >
                                            <TextArea rows={3} maxLength={500} showCount placeholder="Enter shipping address" disabled={sameAsBilling || isReadOnly} />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="country" label="Country">
                                            <Select
                                                options={countries}
                                                size="large" showSearch allowClear
                                                placeholder="Select country"
                                                onChange={handleCountryChange}
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="state" label="State">
                                            <Select
                                                options={stateOptions}
                                                size="large" showSearch allowClear
                                                placeholder="Select state"
                                                filterOption={(input, option) =>
                                                    (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
                                            />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="city" label="City" rules={[{ required: true, message: 'Please enter city' }]}>
                                            <Input size="large" placeholder="Enter city" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="pincode" label="Pincode" rules={[{ required: true, message: 'Please enter pincode' }]}>
                                            <Input size="large" placeholder="Enter pincode" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<DollarOutlined />} no={3} text="Business & Payment Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="payment_terms" label="Payment Terms">
                                            <Select options={paymentTermsOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type payment terms" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="credit_limit" label="Credit Limit (₹)">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="Enter credit limit" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="opening_balance" label="Opening Balance (₹)">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="0.00" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="payment_method" label="Payment Method">
                                            <Select options={paymentMethodOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type payment method" />
                                        </Form.Item>
                                    </Col>

                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="bank_name" label="Bank Name">
                                            <Input size="large" placeholder="Enter bank name" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="bank_account_number" label="Bank Account Number">
                                            <Input size="large" placeholder="Enter account number" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="ifsc_code" label="IFSC Code">
                                            <Input size="large" placeholder="Enter IFSC code" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="account_holder_name" label="Account Holder Name">
                                            <Input size="large" placeholder="Enter account holder name" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>

                            <Card title={<CardTitle icon={<InfoCircleOutlined />} no={4} text="Additional Information" />}>
                                <Row gutter={16}>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="preferred_contact_method" label="Preferred Contact Method">
                                            <Select options={contactMethodOptions} mode="tags" maxCount={1} size="large" showSearch allowClear placeholder="Select or type contact method" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="lead_time_days" label="Lead Time (Days)">
                                            <InputNumber size="large" min={0} style={{ width: '100%' }} placeholder="Enter lead time in days" />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="notes" label="Notes">
                                            <TextArea rows={3} maxLength={500} showCount placeholder="Enter additional notes..." />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Form.Item name="is_active" label="Status" valuePropName="checked">
                                            <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            </Card>
                        </Form>

                        {!isReadOnly && (
                            <div style={{ display: 'flex', justifyContent: 'right', marginTop: 8 }}>
                                <Space>
                                    <Button size='large' onClick={() => form.resetFields()}>Reset</Button>
                                    {editSupplier && (
                                        <Button size="large" danger onClick={handleDelete}>Delete Supplier</Button>
                                    )}
                                    <Button
                                        type="primary"
                                        size="large"
                                        loading={saving}
                                        onClick={() => form.submit()}
                                        style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        {editSupplier ? "Update Supplier" : "Save Supplier"}
                                    </Button>
                                </Space>
                            </div>
                        )}
                    </Col>

                    <Col xs={24} lg={7}>
                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><FileTextOutlined /> Supplier Summary</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {[
                                    ["Supplier Name", summary.supplier_name || "-"],
                                    ["Supplier Code", supplierCode || "-"],
                                    ["Category", firstOf(summary.category) || "-"],
                                    ["Contact Person", summary.contact_person || "-"],
                                    ["Mobile Number", summary.mobile_number || "-"],
                                    ["Email Address", summary.email_address || "-"],
                                    ["GSTIN / Tax ID", summary.gstin_tax_id || "-"],
                                ].map(([label, value]) => (
                                    <div key={label} style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                        <Text type="secondary">{label}</Text>
                                        <Text strong>{value}</Text>
                                    </div>
                                ))}
                                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                                    <Text type="secondary">Status</Text>
                                    <Text strong style={{ color: summary.is_active === false ? "#f5222d" : "#52c41a" }}>
                                        {summary.is_active === false ? "Inactive" : "Active"}
                                    </Text>
                                </div>
                            </Space>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><BulbOutlined /> Helpful Tips</span>}
                            size="small"
                        >
                            <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: "#555", lineHeight: 1.9 }}>
                                <li>Add complete contact details for better communication.</li>
                                <li>Set payment terms to manage cash flow effectively.</li>
                                <li>Credit limit helps to control outstanding amounts.</li>
                                <li>Keep GSTIN / Tax ID updated for accurate tax calculations.</li>
                                <li>Add bank details for faster payments.</li>
                            </ul>
                        </Card>

                        <Card
                            title={<span style={{ color: "rgba(14, 93, 146, 0.95)", fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}><LinkOutlined /> Quick Links</span>}
                            size="small"
                        >
                            <Space direction="vertical" size={10} style={{ width: "100%" }}>
                                {quickLinks.map(({ label, to }) => (
                                    <div
                                        key={label}
                                        onClick={() => navigate(to)}
                                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", fontSize: 13, color: "rgba(14, 93, 146, 0.95)" }}
                                    >
                                        <span>{label}</span>
                                        <span>›</span>
                                    </div>
                                ))}
                            </Space>
                        </Card>
                    </Col>
                </Row>
            </Card>
        </>
    );
};

export default AddSupplier;